import { API_BASE } from './config';
import { getSession } from './auth';

/**
 * Wrapper around fetch that injects the Authorization header with the current
 * user's ID token.
 */
export async function fetchWithAuth(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const session = await getSession();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (session?.idToken) {
    headers['Authorization'] = `Bearer ${session.idToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // MFA grace expired: surface a typed error the app routes on (forced enrolment),
    // rather than a generic failure banner.
    if (response.status === 403 && (body as Record<string, string>).error === 'mfa-required') {
      const err = new Error('mfa-required') as Error & { mfaRequired?: boolean; deadline?: string };
      err.mfaRequired = true;
      err.deadline = (body as Record<string, string>).deadline;
      throw err;
    }
    const message =
      (body as Record<string, string>).error ||
      (body as Record<string, string>).message ||
      `Request failed: ${response.status}`;
    throw new Error(message);
  }

  return response;
}

// ------------------------------------------------------------------
// API operations
// ------------------------------------------------------------------

export interface MfaInfo {
  enabled: boolean;
  federated: boolean;
  deadline: string | null;
  graceExpired: boolean;
}

export interface UserInfo {
  email: string;
  name: string;
  groups: string[];
  mfa?: MfaInfo;
}

export async function getMe(): Promise<UserInfo> {
  const res = await fetchWithAuth('/me');
  return res.json();
}

export interface AccessRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  reason: string;
  scope: 'read-only' | 'read-write';
  status: 'pending' | 'approved' | 'denied';
  adminNote?: string;
  mfaComplete?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Map Lambda response fields to frontend AccessRequest fields */
function mapRequest(raw: Record<string, unknown>): AccessRequest {
  return {
    id: (raw.requestId ?? raw.id ?? '') as string,
    userId: (raw.requesterEmail ?? raw.userId ?? '') as string,
    userName: (raw.requesterName ?? raw.userName ?? '') as string,
    userEmail: (raw.requesterEmail ?? raw.userEmail ?? '') as string,
    reason: (raw.reason ?? '') as string,
    scope: (raw.scope ?? 'read-only') as AccessRequest['scope'],
    status: (raw.status ?? 'pending') as AccessRequest['status'],
    adminNote: (raw.adminNote ?? raw.note) as string | undefined,
    mfaComplete: raw.mfaComplete as boolean | undefined,
    createdAt: (raw.createdAt ?? '') as string,
    updatedAt: (raw.updatedAt ?? '') as string,
  };
}

export interface CreateRequestPayload {
  name: string;
  email: string;
  reason: string;
  scope: 'read-only' | 'read-write';
  categoryPreferences?: Record<string, { read: boolean; write: boolean }>;
}

export async function createRequest(data: CreateRequestPayload): Promise<AccessRequest> {
  const res = await fetchWithAuth('/requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function getMyRequests(): Promise<AccessRequest[]> {
  const res = await fetchWithAuth('/requests?view=mine');
  const body = await res.json();
  const items = body.requests ?? body ?? [];
  return (items as Record<string, unknown>[]).map(mapRequest);
}

export async function getRequests(status?: string): Promise<AccessRequest[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetchWithAuth(`/requests${query}`);
  const body = await res.json();
  const items = body.requests ?? body ?? [];
  return (items as Record<string, unknown>[]).map(mapRequest);
}

export async function approveRequest(id: string, note?: string): Promise<void> {
  await fetchWithAuth(`/requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ action: 'approve', note: note ?? '' }),
  });
}

export async function denyRequest(id: string, note?: string): Promise<void> {
  await fetchWithAuth(`/requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ action: 'deny', note: note ?? '' }),
  });
}

// ------------------------------------------------------------------
// MFA setup operations
// ------------------------------------------------------------------

export interface MfaInitAuthResponse {
  session: string;
  challenge: string;
}

export async function mfaInitAuth(requestId: string, tempPassword: string): Promise<MfaInitAuthResponse> {
  const res = await fetchWithAuth('/mfa/init-auth', {
    method: 'POST',
    body: JSON.stringify({ requestId, tempPassword }),
  });
  return res.json();
}

export interface MfaNewPasswordResponse {
  session: string;
  totpSecretCode: string;
}

export async function mfaNewPassword(
  requestId: string,
  session: string,
  newPassword: string,
): Promise<MfaNewPasswordResponse> {
  const res = await fetchWithAuth('/mfa/new-password', {
    method: 'POST',
    body: JSON.stringify({ requestId, session, newPassword }),
  });
  return res.json();
}

export interface MfaVerifyTotpResponse {
  success: boolean;
}

export async function mfaVerifyTotp(
  requestId: string,
  session: string,
  code: string,
): Promise<MfaVerifyTotpResponse> {
  const res = await fetchWithAuth('/mfa/verify-totp', {
    method: 'POST',
    body: JSON.stringify({ requestId, session, code }),
  });
  return res.json();
}

export interface MfaResendPasswordResponse {
  success: boolean;
  message: string;
  tempPassword?: string;
}

export async function mfaResendPassword(requestId: string): Promise<MfaResendPasswordResponse> {
  const res = await fetchWithAuth('/mfa/resend-password', {
    method: 'POST',
    body: JSON.stringify({ requestId }),
  });
  return res.json();
}

// ------------------------------------------------------------------
// Atlassian credential operations
// ------------------------------------------------------------------

export interface CredentialStatus {
  configured: boolean;
  atlassianDomain?: string;
  atlassianEmail?: string;
  updatedAt?: string;
}

export async function getCredentialStatus(): Promise<CredentialStatus> {
  const res = await fetchWithAuth('/credentials');
  return res.json();
}

export async function saveCredentials(data: {
  atlassianDomain: string;
  atlassianEmail: string;
  atlassianApiToken: string;
}): Promise<{ success: boolean; message: string }> {
  const res = await fetchWithAuth('/credentials', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deleteCredentials(): Promise<{ success: boolean; message: string }> {
  const res = await fetchWithAuth('/credentials', {
    method: 'DELETE',
  });
  return res.json();
}

// ------------------------------------------------------------------
// Policy operations (admin)
// ------------------------------------------------------------------

export interface CategoryPermission {
  enabled: boolean;
  allowRead: boolean;
  allowWrite: boolean;
}

export interface PolicyDocument {
  policyId: string;
  version: number;
  defaultPermission: 'read-only' | 'read-write';
  categories: Record<string, CategoryPermission>;
  domainWhitelist: string[];
  domainBlacklist: string[];
  updatedAt: string;
  updatedBy: string;
}

export interface ToolCategoryInfo {
  id: string;
  displayName: string;
  readCount: number;
  writeCount: number;
}

export async function getToolCategories(): Promise<ToolCategoryInfo[]> {
  const res = await fetchWithAuth('/tool-categories');
  const body = await res.json();
  return body.categories ?? [];
}

export async function getGlobalPolicy(): Promise<PolicyDocument | null> {
  const res = await fetchWithAuth('/policies');
  const body = await res.json();
  return body.policy ?? null;
}

export async function updateGlobalPolicy(policy: Partial<PolicyDocument>): Promise<{ success: boolean; policy: PolicyDocument }> {
  const res = await fetchWithAuth('/policies', {
    method: 'PUT',
    body: JSON.stringify(policy),
  });
  return res.json();
}

export async function getUserPolicy(userId: string): Promise<PolicyDocument | null> {
  const res = await fetchWithAuth(`/policies/user/${encodeURIComponent(userId)}`);
  const body = await res.json();
  return body.policy ?? null;
}

export async function updateUserPolicy(userId: string, policy: Partial<PolicyDocument>): Promise<void> {
  await fetchWithAuth(`/policies/user/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body: JSON.stringify(policy),
  });
}

export async function deleteUserPolicy(userId: string): Promise<void> {
  await fetchWithAuth(`/policies/user/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

// ------------------------------------------------------------------
// Instance operations (multi-instance)
// ------------------------------------------------------------------

export type InstanceTypeValue = 'production' | 'sandbox' | 'test' | 'demo' | 'client';

export interface InstanceConfig {
  instanceId: string;
  label: string;
  instanceType: InstanceTypeValue;
  domain: string;
  jiraEmail: string;
  confluenceEmail: string;
  /** Which products this instance's token was validated against. Absent on legacy rows → treat as both. */
  products?: ('jira' | 'confluence')[];
  /**
   * Which MCPs may consume this instance's credentials.
   * Absent → automatic (all compatible MCPs). Present → only listed MCPs.
   */
  mcpBindings?: ('atlas-admin' | 'arl')[];
  /** Declared scope — informational only (NOT enforced by MCPs this phase). */
  accessMode?: 'read-only' | 'write-only' | 'read-write';
  /** Optional credential expiry, YYYY-MM-DD. */
  expiresAt?: string;
  /** Auth route per product ('site' classic | 'gateway' scoped). Absent = site. */
  tokenRoutes?: { jira?: 'site' | 'gateway'; confluence?: 'site' | 'gateway' };
  cloudId?: string;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateInstancePayload {
  label: string;
  instanceType: InstanceTypeValue;
  domain: string;
  /** Optional for Confluence-only tokens */
  jiraEmail?: string;
  /** Optional for Confluence-only tokens */
  jiraApiToken?: string;
  sameToken?: boolean;
  confluenceEmail?: string;
  confluenceApiToken?: string;
  /**
   * MCP scope for this credential store.
   * Absent → automatic (all compatible MCPs). Non-empty array → only listed MCPs.
   */
  mcpBindings?: ('atlas-admin' | 'arl')[];
  /** Declared scope — informational only (NOT enforced by MCPs this phase). */
  accessMode?: 'read-only' | 'write-only' | 'read-write';
  /** Optional credential expiry, YYYY-MM-DD. */
  expiresAt?: string;
}

export async function listInstances(): Promise<InstanceConfig[]> {
  const res = await fetchWithAuth('/instances');
  const body = await res.json();
  return body.instances ?? [];
}

export async function createInstance(data: CreateInstancePayload): Promise<InstanceConfig> {
  const res = await fetchWithAuth('/instances', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const body = await res.json();
  return body.instance;
}

export async function updateInstance(instanceId: string, data: Partial<CreateInstancePayload>): Promise<void> {
  await fetchWithAuth(`/instances/${encodeURIComponent(instanceId)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function removeInstance(instanceId: string): Promise<void> {
  await fetchWithAuth(`/instances/${encodeURIComponent(instanceId)}`, {
    method: 'DELETE',
  });
}

export async function setDefaultInstance(instanceId: string): Promise<void> {
  await fetchWithAuth(`/instances/${encodeURIComponent(instanceId)}/set-default`, {
    method: 'POST',
  });
}

// ------------------------------------------------------------------
// User management operations (admin)
// ------------------------------------------------------------------

export interface PortalUser {
  username: string;
  /** Cognito sub — the key MCP policy is stored under (differs from username for federated users). */
  sub: string;
  email: string;
  name: string;
  status: string;
  enabled: boolean;
  mfaEnabled: boolean;
  groups: string[];
  createdAt: string;
  lastModifiedAt: string;
}

export async function listUsers(): Promise<PortalUser[]> {
  const res = await fetchWithAuth('/users');
  const body = await res.json();
  return body.users ?? [];
}

export async function inviteUser(email: string, name: string): Promise<{ username: string }> {
  const res = await fetchWithAuth('/users', {
    method: 'POST',
    body: JSON.stringify({ email, name }),
  });
  return res.json();
}

export type UserAction =
  | 'disable'
  | 'enable'
  | 'resend-invite'
  | 'reset-password'
  | 'add-admin'
  | 'remove-admin';

export async function userAction(username: string, action: UserAction): Promise<void> {
  await fetchWithAuth(`/users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify({ action }),
  });
}

// ------------------------------------------------------------------
// MCP access policies (admin, Phase 3)
// ------------------------------------------------------------------

export type McpId = 'atlas-admin' | 'di-assets' | 'n8n' | 'arl' | 'jev';
export const MCP_IDS: McpId[] = ['atlas-admin', 'di-assets', 'n8n', 'arl', 'jev'];

export interface McpPolicyRule {
  pattern: string;
  effect: 'allow' | 'deny';
}

export interface McpPolicyEntry {
  policyId: string; // 'global#<mcp>' | 'user#<sub>#<mcp>'
  allowed?: boolean;
  toolRules?: McpPolicyRule[];
  domainRules?: McpPolicyRule[];
  protectedGrants?: string[];
  lastUsed?: string;
  policyAudit?: unknown[];
}

export interface McpAccessList {
  policies: McpPolicyEntry[];
  protectedTools: Partial<Record<McpId, string[]>>;
}

export async function listMcpPolicies(): Promise<McpPolicyEntry[]> {
  const res = await fetchWithAuth('/mcp-access');
  const body = await res.json();
  return body.policies ?? [];
}

export async function listMcpAccess(): Promise<McpAccessList> {
  const res = await fetchWithAuth('/mcp-access');
  const body = await res.json();
  return { policies: body.policies ?? [], protectedTools: body.protectedTools ?? {} };
}

export interface McpPolicyWrite {
  allowed?: boolean;
  toolRules?: McpPolicyRule[];
  domainRules?: McpPolicyRule[];
  protectedGrants?: string[];
}

export async function putGlobalMcpPolicy(mcp: McpId, policy: McpPolicyWrite): Promise<void> {
  await fetchWithAuth(`/mcp-access/global/${mcp}`, { method: 'PUT', body: JSON.stringify(policy) });
}

export async function putUserMcpPolicy(sub: string, mcp: McpId, policy: McpPolicyWrite): Promise<void> {
  await fetchWithAuth(`/mcp-access/user/${encodeURIComponent(sub)}/${mcp}`, {
    method: 'PUT',
    body: JSON.stringify(policy),
  });
}

export async function deleteUserMcpPolicy(sub: string, mcp: McpId): Promise<void> {
  await fetchWithAuth(`/mcp-access/user/${encodeURIComponent(sub)}/${mcp}`, { method: 'DELETE' });
}

// ------------------------------------------------------------------
// Phase 4: fleet status + generalized credentials
// ------------------------------------------------------------------

export type CredentialType = 'atlassian' | 'n8n' | 'claude-api' | 'trigger-dev' | 'github' | 'generic';
export type AccessMode = 'read-only' | 'write-only' | 'read-write';

export interface CredentialView {
  credentialId: string;
  source: 'instances' | 'n8n' | 'generic';
  type: CredentialType;
  label: string;
  instanceType?: InstanceTypeValue;
  domain?: string;
  url?: string;
  identity?: string;
  products?: ('jira' | 'confluence')[];
  mcpBindings?: string[];
  accessMode?: AccessMode;
  expiresAt?: string;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCredPayload {
  type: Exclude<CredentialType, 'atlassian'>;
  label: string;
  instanceType: InstanceTypeValue;
  accessMode: AccessMode;
  expiresAt?: string;
  /** n8n type */
  n8nApiUrl?: string;
  n8nApiKey?: string;
  mcpBindings?: string[];
  /** generic types */
  url?: string;
  identity?: string;
  secret?: string;
}

export async function listCreds(): Promise<CredentialView[]> {
  const res = await fetchWithAuth('/creds');
  const body = await res.json();
  return body.credentials ?? [];
}

export async function createCred(payload: CreateCredPayload): Promise<void> {
  await fetchWithAuth('/creds', { method: 'POST', body: JSON.stringify(payload) });
}

export async function deleteCred(credentialId: string): Promise<void> {
  await fetchWithAuth(`/creds/${encodeURIComponent(credentialId)}`, { method: 'DELETE' });
}

export async function testMcpCredential(mcp: McpId): Promise<{ ok: boolean; detail: string }> {
  const res = await fetchWithAuth('/creds/test', { method: 'POST', body: JSON.stringify({ mcp }) });
  return res.json();
}

export interface McpStatusEntry {
  id: McpId;
  healthy: boolean;
  checkedAt: string;
  credential: { state: 'configured-working' | 'configured-untested' | 'not-configured' | 'n/a'; detail: string };
  access: { allowed: boolean; hasOverride: boolean };
}

export async function getMcpStatus(): Promise<McpStatusEntry[]> {
  const res = await fetchWithAuth('/mcp-status');
  const body = await res.json();
  return body.statuses ?? [];
}
